import { Button } from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import useGetHomeworkSharingSettings from "../../hooks/api/homework/useGetHomeworkSharingSettings";
import useUpdateHomeworkSharingSettings from "../../hooks/api/homework/useUpdateHomeworkSharingSettings";
import ConfirmDialog, { ConfirmDialogHandle } from "../ConfirmDialog";

type Props = {
  homeworkId: string;
};

export default function ShareHomeworkButton({ homeworkId }: Props) {
  const { t } = useTranslation();
  const confirmDialogRef = useRef<ConfirmDialogHandle>(null);
  const {
    data: sharingSettings,
    isLoading: areSharingSettingsLoading,
    isError: haveSharingSettingsError,
    refetch: refetchSharingSettings,
  } = useGetHomeworkSharingSettings(homeworkId);
  const {
    mutateAsync: updateHomeworkSharingSettings,
    isPending: isUpdateHomeworkSharingSettingLoading,
  } = useUpdateHomeworkSharingSettings(homeworkId);

  const onShareConfirm = useCallback(() => {
    updateHomeworkSharingSettings({
      enabled: !sharingSettings?.sharingEnabled,
    })
      .then(() => refetchSharingSettings())
      .finally(() => confirmDialogRef.current?.close());
  }, [
    refetchSharingSettings,
    sharingSettings?.sharingEnabled,
    updateHomeworkSharingSettings,
  ]);

  if (areSharingSettingsLoading || haveSharingSettingsError) return null;

  return (
    <>
      {}
      <Button
        bg="white"
        color="primary.DEFAULT"
        borderColor="primary.DEFAULT"
        borderWidth={2}
        onClick={() => confirmDialogRef.current?.open()}
        isLoading={isUpdateHomeworkSharingSettingLoading}
      >
        {sharingSettings?.sharingEnabled
          ? t("common.buttons.stop_sharing")
          : t("common.buttons.share")}
      </Button>
      <ConfirmDialog
        ref={confirmDialogRef}
        title={t("homework_page.sharing")}
        message={
          sharingSettings?.sharingEnabled
            ? t("homework_page.stop_sharing_message")
            : t("homework_page.sharing_message")
        }
        onConfirm={onShareConfirm}
        isLoading={isUpdateHomeworkSharingSettingLoading}
      />
    </>
  );
}
